import {useState} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import {useSuiContext} from '@s-ui/react-context'
import {getImagesUrlsAdapted, imageUrlAdapterTransformTypes} from '@adv-mt/mt-hook-use-image-url-adapter'
import IconCircleCheckOutline from '@adv-mt/mt-svg-icons/lib/CircleCheckOutline.js'
import IconDirectionsOutline from '@adv-mt/mt-svg-icons/lib/DirectionsOutline.js'
import IconShopOutline from '@adv-mt/mt-svg-icons/lib/ShopOutline.js'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'
import {Avatar, AVATAR_SIZES} from '@adv-mt/ui/avatar'
import {Badge, BADGE_DESIGNS, BADGE_INTENTS, BADGE_SIZES} from '@adv-mt/ui/badge'
import {Button, BUTTON_SHAPES, BUTTON_SIZES, BUTTON_VARIANTS} from '@adv-mt/ui/button'
import {Icon, ICON_SIZES} from '@adv-mt/ui/icon'
import {LOADING, MediaImage, OBJECT_FIT} from '@adv-mt/ui/media-image'
import {TooltipProvider} from '@adv-mt/ui/tooltip'
import {Body1, Body2, Headline1, Headline2, Subhead1} from '@adv-mt/ui/typography'

import ActionTooltip from '../../../components/ActionTooltip.js'
import {BASE_CLASS as PAGE_BASE_CLASS} from '../../../config.js'
import {DRAWER_TYPES} from '../../../drawers/config.js'
import FavoriteDealerButton from './FavoriteDealerButton.js'

const BASE_CLASS = `${PAGE_BASE_CLASS}-sellerInfo`
const MAX_SERVICES_DISPLAYED = 6
const MIN_PROVINCES_FOR_SHIPPING = 1
const SELLER_LOGO_WIDTH = 107 // px — keep in sync with $w-mt-detail-new-seller-logo in ./index.scss
const SELLER_LOGO_ASPECT_RATIO = 1.3375 // aspect ratio between $w-mt-detail-new-seller-logo & $h-mt-detail-new-seller-logo in ./index.scss
const LOGO_ADAPTER_OPTIONS = {
  aspectRatio: SELLER_LOGO_ASPECT_RATIO,
  imageWidth: SELLER_LOGO_WIDTH * 2, // 2x for HiDPI screens, matching packages/ui/components/hero/dealer
  transformType: imageUrlAdapterTransformTypes.DEVICE_CROP
}

const SellerInfo = ({ad = {}, isDealerFavorite = false, onOpenDrawer, seller = {}, trackingData = {}}) => {
  const {domain, i18n} = useSuiContext()
  const [isAddressShown, setIsAddressShown] = useState(false)
  const {track: analyticsTrack} = useAnalyticsTrack()

  const config = domain.get('config')
  const sellerSectionId = config.get('COMMON').AD_DETAIL_PAGE_SECTIONS.AD_DETAIL_PAGE_SECTION_IDS.SELLER
  const {SHOW_MULTI_PROVINCE_INFO_ADS} = config.get('search')
  const {HIT_ORIGINS, HIT_INFORMATIONS, TRACKING_EVENT_NAMES} = config.get('tracking')

  const {isDealer} = seller
  const {
    id: dealerId,
    location = {},
    logoImageUrl,
    name,
    services = [],
    stockPageUrl,
    showDealerDirections
  } = seller.dealer ?? {}

  const [adaptedLogo] = logoImageUrl
    ? getImagesUrlsAdapted({images: [{src: logoImageUrl}], options: LOGO_ADAPTER_OPTIONS})
    : []
  const adaptedLogoImageUrl = adaptedLogo?.src ?? logoImageUrl

  const displayName = isDealer ? name : seller.user?.name
  const displayLocation = isDealer ? location.province : seller.user?.province
  const fullAddress = isDealer
    ? [location.address, [location.postalCode, location.city].filter(Boolean).join(' ')].filter(Boolean).join(', ')
    : ''

  const includedServices = services.filter(({isIncluded}) => isIncluded)
  const hiddenServicesCount = includedServices.length - MAX_SERVICES_DISPLAYED
  const hasShipping =
    Boolean(SHOW_MULTI_PROVINCE_INFO_ADS) && (ad.location?.provinceIds?.length ?? 0) > MIN_PROVINCES_FOR_SHIPPING
  const isCanarias = config.get('CANARIAS_PROVINCE_IDS').includes(ad.provinceId)
  const showServices = isDealer && (hasShipping || includedServices.length > 0)

  const handleShowAllServices = async () => {
    onOpenDrawer({data: {hasShipping, services: includedServices}, type: DRAWER_TYPES.SELLER_SERVICES})

    const eventData = await domain.get('get_event_tracking_data_use_case').execute({
      event: TRACKING_EVENT_NAMES.DEALER_SERVICES_POPUP,
      origin: HIT_ORIGINS.AD_DETAIL
    })

    analyticsTrack(eventData)
  }

  const handleShowFullAddress = async () => {
    setIsAddressShown(true)

    const eventData = await domain.get('get_event_tracking_data_use_case').execute({
      event: TRACKING_EVENT_NAMES.AD_DETAIL_BUTTON_CLICKED,
      hitInformation: HIT_INFORMATIONS.AD_DETAIL_SHOW_SELLER_ADDRESS
    })

    analyticsTrack(eventData)
  }

  const className = cx(`${BASE_CLASS}`, {
    [`${BASE_CLASS}--dealer`]: isDealer
  })

  if (!displayName) {
    return null
  }

  return (
    <section className={className} id={sellerSectionId}>
      <Headline1>{i18n.t('SELLER.WHO_SELLS_TITLE')}</Headline1>

      <div className={`${BASE_CLASS}-content`}>
        <div className={`${BASE_CLASS}-profile`}>
          <div className={`${BASE_CLASS}-info`}>
            {isDealer && !!logoImageUrl && (
              <div className={`${BASE_CLASS}-logo`}>
                <MediaImage alt="" loading={LOADING.LAZY} objectFit={OBJECT_FIT.CONTAIN} src={adaptedLogoImageUrl} />
              </div>
            )}
            {!isDealer && <Avatar alt="" size={AVATAR_SIZES.XXLARGE} src={seller.user?.avatar} name={displayName} />}

            <div className={`${BASE_CLASS}-identity`}>
              {isDealer ? (
                <Badge design={BADGE_DESIGNS.SOFT} iconLeft={<IconCircleCheckOutline />} intent={BADGE_INTENTS.SUCCESS}>
                  {i18n.t('COMMON_PROFESSIONAL')}
                </Badge>
              ) : (
                <Badge design={BADGE_DESIGNS.SOFT} intent={BADGE_INTENTS.NEUTRAL}>
                  {i18n.t('COMMON_PRIVATE')}
                </Badge>
              )}

              <div className={`${BASE_CLASS}-text`}>
                {Boolean(stockPageUrl) && isDealer ? (
                  <Headline2>
                    <a href={stockPageUrl} className={`${BASE_CLASS}-name`}>
                      {displayName}
                    </a>
                  </Headline2>
                ) : (
                  <Headline2>{displayName}</Headline2>
                )}
                {Boolean(displayLocation) && <Body1>{displayLocation}</Body1>}
                {Boolean(fullAddress) &&
                  (isAddressShown ? (
                    <Body2>{fullAddress}</Body2>
                  ) : (
                    <Body2
                      link
                      highlight
                      as="button"
                      type="button"
                      onClick={handleShowFullAddress}
                      className={`${BASE_CLASS}-addressLink`}
                    >
                      {i18n.t('SELLER.SHOW_ADDRESS')}
                    </Body2>
                  ))}
              </div>
            </div>
          </div>

          {isDealer && (
            <TooltipProvider>
              <div className={`${BASE_CLASS}-actions`}>
                {showDealerDirections && Boolean(location.mapsDirectionsUrl) && (
                  <ActionTooltip label={i18n.t('DEALER.ADDRESS_INDICATIONS')}>
                    <Button
                      aria-label={i18n.t('DEALER.ADDRESS_INDICATIONS')}
                      href={location.mapsDirectionsUrl}
                      rel="noopener noreferrer"
                      shape={BUTTON_SHAPES.CIRCLE}
                      size={BUTTON_SIZES.MEDIUM}
                      target="_blank"
                      variant={BUTTON_VARIANTS.FLAT}
                    >
                      <Icon size={ICON_SIZES.MD}>
                        <IconDirectionsOutline size={ICON_SIZES.MD} />
                      </Icon>
                    </Button>
                  </ActionTooltip>
                )}

                {Boolean(stockPageUrl) && (
                  <ActionTooltip label={i18n.t('COMMON.MORE_SELLER_VEHICLES')}>
                    <Button
                      aria-label={i18n.t('COMMON.MORE_SELLER_VEHICLES')}
                      href={stockPageUrl}
                      shape={BUTTON_SHAPES.CIRCLE}
                      size={BUTTON_SIZES.MEDIUM}
                      variant={BUTTON_VARIANTS.FLAT}
                    >
                      <Icon size={ICON_SIZES.MD}>
                        <IconShopOutline size={ICON_SIZES.MD} />
                      </Icon>
                    </Button>
                  </ActionTooltip>
                )}

                {Boolean(dealerId) && (
                  <ActionTooltip
                    label={i18n.t(isDealerFavorite ? 'COMMON.REMOVE_FAVORITE_DEALER' : 'COMMON.ADD_FAVORITE_DEALER')}
                  >
                    <FavoriteDealerButton
                      contractId={dealerId}
                      isFavorite={isDealerFavorite}
                      trackingData={trackingData}
                    />
                  </ActionTooltip>
                )}
              </div>
            </TooltipProvider>
          )}
        </div>

        {showServices && (
          <div className={`${BASE_CLASS}-services`}>
            <Subhead1 as="h3" className={`${BASE_CLASS}-servicesTitle`}>
              {i18n.t('SELLER.SERVICES_OFFERED_TITLE')}
            </Subhead1>

            {hasShipping && (
              <div className={`${BASE_CLASS}-shipping`}>
                <Body2 highlight>
                  {i18n.t(isCanarias ? 'COMMON.FLEXIBLE_DELIVERY_CANARIAS' : 'COMMON.FLEXIBLE_DELIVERY')}
                </Body2>
                <Body2>
                  {i18n.t(
                    isCanarias
                      ? 'DEALER_SERVICE.DEALER_SERVICES_LOCATION_CANARIAS'
                      : 'DEALER_SERVICE.DEALER_SERVICES_LOCATION'
                  )}
                </Body2>
              </div>
            )}

            {includedServices.length > 0 && (
              <div className={`${BASE_CLASS}-serviceBadges`}>
                {includedServices.slice(0, MAX_SERVICES_DISPLAYED).map(({title, type}) => (
                  <Badge intent={BADGE_INTENTS.SURFACE} key={type} size={BADGE_SIZES.LARGE}>
                    {title}
                  </Badge>
                ))}
                {hiddenServicesCount > 0 && (
                  <Badge intent={BADGE_INTENTS.SURFACE} size={BADGE_SIZES.LARGE}>{`+${hiddenServicesCount}`}</Badge>
                )}
              </div>
            )}

            <Body2 as="button" className={`${BASE_CLASS}-servicesLink`} highlight onClick={handleShowAllServices}>
              {i18n.t('COMMON.KNOW_MORE')}
            </Body2>
          </div>
        )}
      </div>
    </section>
  )
}

SellerInfo.displayName = 'SellerInfo'

SellerInfo.propTypes = {
  ad: PropTypes.shape({
    location: PropTypes.shape({provinceIds: PropTypes.array}),
    provinceId: PropTypes.number
  }),
  isDealerFavorite: PropTypes.bool,
  onOpenDrawer: PropTypes.func.isRequired,
  seller: PropTypes.shape({
    dealer: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      location: PropTypes.shape({
        address: PropTypes.string,
        city: PropTypes.string,
        mapsDirectionsUrl: PropTypes.string,
        postalCode: PropTypes.string,
        province: PropTypes.string
      }),
      logoImageUrl: PropTypes.string,
      name: PropTypes.string,
      services: PropTypes.arrayOf(
        PropTypes.shape({
          description: PropTypes.string,
          isIncluded: PropTypes.bool,
          title: PropTypes.string,
          type: PropTypes.string
        })
      ),
      stockPageUrl: PropTypes.string
    }),
    isDealer: PropTypes.bool,
    user: PropTypes.shape({avatar: PropTypes.string, name: PropTypes.string, province: PropTypes.string})
  }),
  trackingData: PropTypes.object
}

export default SellerInfo
